import { useEffect, useState } from "react";
import { FileCheck2, Pencil, Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, downloadPdf, fmtDate, formatApiError, inr, waLink } from "@/lib/api";
import { Empty, PageHeader, Panel, StatusBadge, marginCls } from "@/components/admin/common";
import { BookingDialog } from "@/components/admin/BookingDialog";
import { PaymentsDialog } from "@/components/admin/PaymentsDialog";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, booking: null });
  const [paying, setPaying] = useState(null);

  const load = () => api.get("/admin/bookings").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setItems((l) => l.filter((b) => b.id !== id));
    toast.success("Booking deleted");
  };
  const voucher = async (b) => {
    try { await downloadPdf(`/admin/bookings/${b.id}/voucher.pdf`, `${b.booking_no}-confirmation.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const waText = (b) => `Hi ${b.customer_name}, your Andaman booking ${b.booking_no} with Ocean Wings Travel for ${fmtDate(b.travel_date)} is ${b.status}. ` +
    `Total: ${inr(b.total_price)}, received: ${inr(b.advance_paid)}, balance due: ${inr(b.balance_due)}. I'm sharing your booking confirmation PDF here.`;
  const active = items.filter((b) => b.status !== "cancelled");
  const sum = (k) => active.reduce((a, b) => a + (b[k] || 0), 0);

  return (
    <div data-testid="admin-bookings-page">
      <PageHeader title="Bookings" subtitle="Confirmed trips, payments and profit per booking.">
        <Button className="bg-ocean hover:bg-navy" onClick={() => setDialog({ open: true, booking: null })} data-testid="booking-new-button"><Plus className="h-4 w-4 mr-2" />New booking</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[["Revenue", sum("total_price"), "text-navy"], ["Cost", sum("total_cost"), "text-navy"], ["Profit", sum("profit"), "text-emerald-600"], ["Balance due", sum("balance_due"), "text-coral"]].map(([l, v, c]) => (
          <Panel key={l} className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{l}</p><p className={`mt-2 font-mono text-xl font-semibold ${c}`} data-testid={`bookings-total-${l.toLowerCase().replace(" ", "-")}`}>{inr(v)}</p></Panel>
        ))}
      </div>
      <Panel className="overflow-x-auto">
        {!loading && items.length === 0 ? <Empty testId="bookings-empty">No bookings yet. Convert an enquiry or add a booking manually.</Empty> : (
          <Table className="min-w-[1050px]">
            <TableHeader><TableRow>
              <TableHead>Booking</TableHead><TableHead>Customer</TableHead><TableHead>Package</TableHead><TableHead>Travel</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Profit</TableHead><TableHead className="text-right">Paid / Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">{b.booking_no}</TableCell>
                  <TableCell><p className="font-medium text-navy">{b.customer_name}</p><p className="text-xs text-slate-500">{b.phone}</p></TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">{b.package_name || "Custom"}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(b.travel_date)}<p className="text-xs text-slate-500">{b.guests} guests</p></TableCell>
                  <TableCell className="text-right font-mono">{inr(b.total_price)}</TableCell>
                  <TableCell className={`text-right font-mono ${marginCls(b.margin)}`}>{inr(b.profit)}<p className="text-xs">{b.margin}%</p></TableCell>
                  <TableCell className="text-right font-mono text-sm">{inr(b.advance_paid)}<p className="text-xs text-coral">{inr(b.balance_due)} due</p></TableCell>
                  <TableCell><StatusBadge status={b.status} kind="booking" testId={`booking-status-${b.id}`} /></TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="Payments" onClick={() => setPaying(b)} data-testid={`booking-payments-${b.id}`}><Wallet className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-ocean" title="Download booking confirmation PDF" onClick={() => voucher(b)} data-testid={`booking-voucher-${b.id}`}><FileCheck2 className="h-4 w-4" /></Button>
                      <a href={waLink(b.phone, waText(b))} title="Send on WhatsApp" target="_blank" rel="noreferrer" data-testid={`booking-whatsapp-${b.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-emerald-600 hover:bg-emerald-50"><WhatsAppIcon className="h-4 w-4" /></a>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ open: true, booking: b })} data-testid={`booking-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(b.id)} data-testid={`booking-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <PaymentsDialog booking={paying} onClose={() => setPaying(null)} onChanged={(nb) => setItems((l) => l.map((x) => (x.id === nb.id ? nb : x)))} />
      <BookingDialog open={dialog.open} booking={dialog.booking} onOpenChange={(o) => !o && setDialog({ open: false, booking: null })}
        onSaved={() => { setDialog({ open: false, booking: null }); load(); }} />
    </div>
  );
}
